import { ORDER_TYPE } from "constants/order";
import { joinMultiSelectIds } from "components/shared-components/HubMultiSelectFilter";

export const LEGEND_COLS = 3;

export const DASHBOARD_DEFAULT_ORDER_TYPES = [
  String(ORDER_TYPE.RECEIVE.value),
  String(ORDER_TYPE.RETURN.value),
];

export const ORDER_TYPE_FILTER_OPTIONS = [
  { value: String(ORDER_TYPE.RETURN.value), label: ORDER_TYPE.RETURN.label },
  { value: String(ORDER_TYPE.RECEIVE.value), label: ORDER_TYPE.RECEIVE.label },
  { value: String(ORDER_TYPE.COLLECTOR.value), label: ORDER_TYPE.COLLECTOR.label },
];

export const DASHBOARD_ALL_ORDER_TYPE_VALUES = ORDER_TYPE_FILTER_OPTIONS.map(
  option => option.value
);

export function isDashboardAllOrderTypes(selectedOrderTypes) {
  return selectedOrderTypes == null;
}

function joinOrderTypeIds(orderTypeValues) {
  return Array.from(new Set(orderTypeValues))
    .sort((left, right) => Number(left) - Number(right))
    .join(",");
}

export function resolveDashboardOrderTypeParam(selectedOrderTypes) {
  if (isDashboardAllOrderTypes(selectedOrderTypes)) {
    return joinOrderTypeIds(DASHBOARD_ALL_ORDER_TYPE_VALUES);
  }

  const types = (Array.isArray(selectedOrderTypes) ? selectedOrderTypes : [])
    .map(value => String(value ?? "").trim())
    .filter(value => ORDER_TYPE_FILTER_OPTIONS.some(option => option.value === value));

  const source = types.length > 0 ? types : DASHBOARD_DEFAULT_ORDER_TYPES;

  return joinOrderTypeIds(source);
}

export const INITIAL_STATE = {
  selectedStatusFilter: "all",
  detailChartMode: "status",
  selectedHubIds: [],
  selectedOrderTypes: [...DASHBOARD_DEFAULT_ORDER_TYPES],
  manualDateRange: { startDate: "", endDate: "" },
  hasManualDateRange: false,
  visibleKeys: [],
  shouldForceSevenDayStatusRange: false,
};

export const FALLBACK_STATUS_FILTER_OPTIONS = [
  { value: "1", label: "Tạo đơn" },
  { value: "2", label: "Đã điều phối" },
  { value: "3", label: "Đã nhận hàng" },
  { value: "4", label: "Đã giao hàng" },
  { value: "5", label: "Hoàn tất" },
  { value: "6", label: "Đang chờ" },
  { value: "7", label: "Hủy đơn" },
  { value: "8", label: "Đến hub" },
  { value: "9", label: "Trung chuyển" },
];

export const STATUS_CHART_DEFAULT_HIDDEN_VALUES = new Set(["5", "7"]);

export const STATUS_CHART_DEFAULT_HIDDEN_LABELS_NORMALIZED = new Set(["hoàn tất", "hủy đơn"]);

export function getDashboardHubFilterIds({ selectedHubIds, hubSelectOptions }) {
  return joinMultiSelectIds({
    selectedIds: selectedHubIds,
    options: hubSelectOptions,
  });
}

export function buildPendingCompletionOrdersListUrl(hubId) {
  const params = new URLSearchParams({ delivered_pending_completion: "true" });
  if (hubId) {
    params.set("hub_id", hubId);
  }
  return `${window.location.origin}/manage-orders?${params.toString()}`;
}

// Mặc định sẽ ẩn trạng thái hoàn tất và hủy đơn
export function isStatusChartKeyHiddenByDefault(statusKey, statusLabelByValue) {
  const keyStr = String(statusKey).trim();
  if (STATUS_CHART_DEFAULT_HIDDEN_VALUES.has(keyStr)) {
    return true;
  }
  const labelFromMap = String(statusLabelByValue[keyStr] ?? "").trim().toLowerCase();
  if (labelFromMap && STATUS_CHART_DEFAULT_HIDDEN_LABELS_NORMALIZED.has(labelFromMap)) {
    return true;
  }
  if (STATUS_CHART_DEFAULT_HIDDEN_LABELS_NORMALIZED.has(keyStr.toLowerCase())) {
    return true;
  }
  return false;
}

// Hiển thị ngày dạng DD/MM (không hợp lệ → "—")
export function formatDateLabel(dateValue) {
  if (dateValue == null || dateValue === "") {
    return "—";
  }
  const dateObj = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(dateObj.getTime())) {
    return "—";
  }
  const day = `${dateObj.getDate()}`.padStart(2, "0");
  const month = `${dateObj.getMonth() + 1}`.padStart(2, "0");
  return `${day}/${month}`;
}

// Làm tròn số cho nhãn hover trục Y
export function formatHoverGuideValue(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "0";
  }
  return `${Math.round(numericValue)}`;
}

// Ngày hiện tại ISO YYYY-MM-DD
export function getTodayIsoDate() {
  return new Date().toISOString().split("T")[0];
}

// 7 ngày gần nhất kết thúc tại todayIsoDate.
export function getLast7DaysRange(todayIsoDate) {
  const end = new Date(todayIsoDate);
  const start = new Date(todayIsoDate);
  start.setDate(end.getDate() - 6);
  return {
    startDate: start.toISOString().split("T")[0],
    endDate: end.toISOString().split("T")[0],
  };
}

// N ngày gần nhất kết thúc tại todayIsoDate.
export function getLastNDaysRange(todayIsoDate, dayCount) {
  const end = new Date(todayIsoDate);
  const start = new Date(todayIsoDate);
  start.setDate(end.getDate() - (dayCount - 1));
  return {
    startDate: start.toISOString().split("T")[0],
    endDate: end.toISOString().split("T")[0],
  };
}

// Lọc dữ liệu timeline theo khoảng start/end.
export function filterDateRange(data, range) {
  return data.filter(item => item.date >= range.startDate && item.date <= range.endDate);
}

// Padding khi gộp bbox lưới + tooltip
export const CHART_TOOLTIP_UNION_PADDING_PX = 18;

// Chỉ số mốc timeline khi con trỏ nằm trong bán kính chạm chặt quanh điểm
export function resolveStrictTimelineMarkerIndex(xWithinGrid, stepX, pointCount) {
  if (pointCount <= 1) {
    return 0;
  }
  if (stepX <= 0) {
    return 0;
  }

  let nearestIdx = 0;
  let minDist = Infinity;
  for (let i = 0; i < pointCount; i += 1) {
    const centerX = i * stepX;
    const dist = Math.abs(xWithinGrid - centerX);
    if (dist < minDist) {
      minDist = dist;
      nearestIdx = i;
    }
  }

  const hitRadiusPx = Math.max(8, Math.min(26, stepX * 0.28));

  return minDist <= hitRadiusPx ? nearestIdx : null;
}

// Chọn mốc timeline giữ session tooltip
export function resolveTimelineMilestoneIndex(
  xWithinGrid,
  stepX,
  pointCount,
  previousIndex,
  hasActiveTooltipSession
) {
  const strictIdx = resolveStrictTimelineMarkerIndex(xWithinGrid, stepX, pointCount);
  if (strictIdx !== null) {
    return strictIdx;
  }

  if (hasActiveTooltipSession && previousIndex !== null && previousIndex !== undefined) {
    return Math.max(0, Math.min(pointCount - 1, previousIndex));
  }

  if (previousIndex !== null && previousIndex !== undefined) {
    const prevClamped = Math.max(0, Math.min(pointCount - 1, previousIndex));
    const voronoiIdx = Math.max(
      0,
      Math.min(pointCount - 1, Math.round(xWithinGrid / stepX))
    );
    if (voronoiIdx === prevClamped) {
      return prevClamped;
    }
  }

  return null;
}

// Hình chữ nhật bao lưới chart + panel tooltip 
export function getChartGridTooltipUnionBounds(gridRect, tooltipRect, paddingPx = CHART_TOOLTIP_UNION_PADDING_PX) {
  const pad = paddingPx;
  const expand = rect => ({
    left: rect.left - pad,
    right: rect.right + pad,
    top: rect.top - pad,
    bottom: rect.bottom + pad,
  });

  const gridExpanded = expand(gridRect);

  const hasTooltip =
    tooltipRect
    && tooltipRect.width > 0
    && tooltipRect.height > 0
    && Number.isFinite(tooltipRect.left);

  if (!hasTooltip) {
    return gridExpanded;
  }

  const tooltipExpanded = expand(tooltipRect);

  return {
    left: Math.min(gridExpanded.left, tooltipExpanded.left),
    right: Math.max(gridExpanded.right, tooltipExpanded.right),
    top: Math.min(gridExpanded.top, tooltipExpanded.top),
    bottom: Math.max(gridExpanded.bottom, tooltipExpanded.bottom),
  };
}

// Điểm (x,y) có nằm trong bounds màn hình không
export function isPointInsideScreenBounds(bounds, x, y) {
  return x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom;
}
